import moment from 'moment';
import { getTimezoneByCity } from './timezoneByCity';

moment.locale('ru');

type TzParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function partsInTz(date: Date, timeZone: string): TzParts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const map: Record<string, string> = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatParts(p: TzParts, withYear: boolean): string {
  const date = withYear
    ? `${pad2(p.day)}.${pad2(p.month)}.${p.year}`
    : `${pad2(p.day)}.${pad2(p.month)}`;
  return `${date} ${pad2(p.hour)}:${pad2(p.minute)}`;
}

function currentYearInTz(timeZone: string): number {
  return partsInTz(new Date(), timeZone).year;
}

/**
 * Дата/время в таймзоне города ПВЗ (не браузера).
 * @param timeZoneOrCity IANA (Europe/Moscow) или название города («Пермь»)
 */
export const formatDate = (
  dateString: string,
  timeZoneOrCity: string = 'Europe/Moscow',
): string => {
  const timeZone = timeZoneOrCity.includes('/')
    ? timeZoneOrCity
    : getTimezoneByCity(timeZoneOrCity);
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) {
    const date = moment(dateString);
    const format =
      date.year() === moment().year() ? 'DD.MM HH:mm' : 'DD.MM.YYYY HH:mm';
    return date.format(format);
  }
  const p = partsInTz(d, timeZone);
  return formatParts(p, p.year !== currentYearInTz(timeZone));
};

export const formatDateOnly = (
  dateString: string,
  timeZoneOrCity: string = 'Europe/Moscow',
): string => {
  const timeZone = timeZoneOrCity.includes('/')
    ? timeZoneOrCity
    : getTimezoneByCity(timeZoneOrCity);
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) {
    const date = moment(dateString);
    const format =
      date.year() === moment().year() ? 'DD.MM' : 'DD.MM.YYYY';
    return date.format(format);
  }
  const p = partsInTz(d, timeZone);
  return p.year !== currentYearInTz(timeZone)
    ? `${pad2(p.day)}.${pad2(p.month)}.${p.year}`
    : `${pad2(p.day)}.${pad2(p.month)}`;
};

/** Интервал Достависты в таймзоне города ПВЗ */
export function formatDvIntervalLine(
  startIso: string,
  endIso: string,
  timeZoneOrCity: string = 'Europe/Moscow',
): string {
  const timeZone = timeZoneOrCity.includes('/')
    ? timeZoneOrCity
    : getTimezoneByCity(timeZoneOrCity);
  const aDate = new Date(startIso);
  const bDate = new Date(endIso);
  if (Number.isNaN(aDate.getTime())) return formatDate(startIso, timeZone);
  const a = partsInTz(aDate, timeZone);
  if (Number.isNaN(bDate.getTime())) {
    return formatParts(a, a.year !== currentYearInTz(timeZone));
  }
  const b = partsInTz(bDate, timeZone);
  const sameDay =
    a.year === b.year && a.month === b.month && a.day === b.day;
  if (sameDay) {
    const datePart =
      a.year !== currentYearInTz(timeZone)
        ? `${pad2(a.day)}.${pad2(a.month)}.${a.year}`
        : `${pad2(a.day)}.${pad2(a.month)}`;
    return `${datePart} ${pad2(a.hour)}:${pad2(a.minute)} – ${pad2(b.hour)}:${pad2(b.minute)}`;
  }
  return `${formatParts(a, a.year !== currentYearInTz(timeZone))} – ${formatParts(b, b.year !== currentYearInTz(timeZone))}`;
}

export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

/** Финансы: всегда с копейками, чтобы сходилось с ledger/актами. */
export const formatMoneyRub = (price: number): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';

  const digits = phone.replace(/\D/g, '');
  const normalized = digits.startsWith('8') ? '7' + digits.slice(1) : digits;
  const withCountryCode = normalized.startsWith('7') ? normalized : '7' + normalized;

  if (withCountryCode.length !== 11) {
    return phone;
  }

  const code = withCountryCode.slice(1, 4);
  const part1 = withCountryCode.slice(4, 7);
  const part2 = withCountryCode.slice(7, 9);
  const part3 = withCountryCode.slice(9, 11);

  return `+7 ${code} ${part1}-${part2}-${part3}`;
};

export const toNumberOrNull = (v: string | number | null): number | null => {
  if (v === null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isNaN(n) ? null : n;
};
